import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid2,
  Paper,
  LinearProgress,
  IconButton,
  Alert,
  CardContent,
  alpha,
  CircularProgress,
  Chip,
  Divider,
  useTheme,
  Stack,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PhotoIcon from '@mui/icons-material/Photo';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BasePage from './BasePage';
import { useBusinessAgent } from '../contexts/BusinessAgentContext';
import { useHomeDetails } from '../hooks/useHomeDetails';
import { HomeDetails, ClientAnalytics } from '../types/business';
import ImageEngagementGallery from '../components/ImageEngagementGallery';

const HomeAnalytics = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { clientEmail, homeId } = useParams<{ clientEmail: string; homeId: string }>();
  const { isAgent, getClientAnalytics, isLoadingClientAnalytics } = useBusinessAgent();
  
  // Use the existing hook to fetch home details
  const { homeData, loading: homeLoading, error: homeError } = useHomeDetails(homeId || '');
  
  // Analytics state
  const [clientAnalytics, setClientAnalytics] = useState<ClientAnalytics | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(7); // Default to 7 days
  
  const decodedClientEmail = useMemo(() => {
    return clientEmail ? decodeURIComponent(clientEmail) : '';
  }, [clientEmail]);

  // Fetch analytics data when component mounts or period changes
  useEffect(() => {
    if (isAgent && decodedClientEmail && homeId) {
      const fetchAnalytics = async () => {
        try {
          const analytics = await getClientAnalytics(decodedClientEmail, homeId, selectedPeriod);
          setClientAnalytics(analytics);
        } catch (error) {
          console.error('Failed to fetch client analytics:', error);
        }
      };
      
      fetchAnalytics();
    }
  }, [isAgent, decodedClientEmail, homeId, selectedPeriod, getClientAnalytics]);

  // Parse home details from the API response
  const homeDetails = useMemo((): HomeDetails => {
    if (!homeData?.details) return {};
    
    try {
      return JSON.parse(homeData.details);
    } catch (e) {
      console.error('Error parsing home details:', e);
      return { address: homeData.details }; // Fallback for backward compatibility
    }
  }, [homeData]);

  const homeInfo = useMemo(() => ({
    id: homeId,
    name: homeData?.chat_name || 'Loading...',
    address: homeDetails.address || 'No address specified',
    bedrooms: homeDetails.bedrooms || 0,
    bathrooms: homeDetails.bathrooms || 0,
    sqft: homeDetails.sqft || 0,
    price: homeDetails.price || 0,
    yearBuilt: homeDetails.yearBuilt || null,
    features: homeDetails.features || []
  }), [homeId, homeData, homeDetails]);

  const handleBackToClient = () => {
    navigate(`/business/agent/client/${clientEmail}`);
  };

  const handlePeriodChange = (_event: React.MouseEvent<HTMLElement>, newPeriod: number | null) => {
    if (newPeriod !== null) {
      setSelectedPeriod(newPeriod);
    }
  };


  const getPeriodDescription = (days: number) => {
    switch (days) {
      case 7: return 'Last week\'s activity';
      case 30: return 'Last month\'s trends';
      case 180: return 'Long-term patterns';
      default: return `Last ${days} days`;
    }
  };

  if (!isAgent) {
    return (
      <BasePage
        title="Access Denied | CribCritic"
        description="Agent access required"
      >
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Alert severity="error">
            You need agent access to view analytics.
          </Alert>
        </Container>
      </BasePage>
    );
  }

  if (homeLoading || isLoadingClientAnalytics) {
    return (
      <BasePage
        title="Loading Analytics | CribCritic"
        description="Loading property analytics"
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading property analytics...
              </Typography>
              {isLoadingClientAnalytics && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Fetching client engagement data...
                </Typography>
              )}
            </Box>
          </Box>
        </Container>
      </BasePage>
    );
  }

  if (homeError || !homeData) {
    return (
      <BasePage
        title="Property Not Found | CribCritic"
        description="The requested property could not be found"
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" gutterBottom>
              Property Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {homeError || "The property you're looking for doesn't exist or you don't have access to it."}
            </Typography>
            <IconButton 
              onClick={handleBackToClient}
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>
        </Container>
      </BasePage>
    );
  }

  return (
    <BasePage
      title={`${homeInfo.name} - Analytics | CribCritic`}
      description={`Analytics for ${homeInfo.name} for client ${decodedClientEmail}`}
      keywords="analytics, property analysis, engagement tracking"
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Clean Professional Header */}
        <Box sx={{ mb: 4 }}>
          {/* Navigation & Title Bar */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                onClick={handleBackToClient}
                size="small"
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  }
                }}
              >
                <ArrowBackIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography 
                  variant="h5" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.primary',
                    lineHeight: 1.2
                  }}
                >
                  Property Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {getPeriodDescription(selectedPeriod)}
                </Typography>
              </Box>
            </Box>
            
            <ToggleButtonGroup
              value={selectedPeriod}
              exclusive
              onChange={handlePeriodChange}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  color: theme.palette.grey[400],
                  '&.Mui-selected': {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    }
                  },
                  '&:hover': {
                    color: theme.palette.text.primary,
                    bgcolor: alpha(theme.palette.primary.main, 0.12)
                  }
                }
              }}
            >
              <ToggleButton value={7}>7D</ToggleButton>
              <ToggleButton value={30}>1M</ToggleButton>
              <ToggleButton value={180}>6M</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {/* Property Info Card */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Grid2 container spacing={3} alignItems="center">
              {/* Property Identity */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main',
                      width: 44,
                      height: 44,
                      fontSize: '1.1rem',
                      fontWeight: 600
                    }}
                  >
                    {homeInfo.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.3,
                        mb: 0.5,
                        textAlign: 'left'
                      }}
                    >
                      {homeInfo.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'flex-start' }}>
                      <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {homeInfo.address}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Chip 
                        label={decodedClientEmail}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          height: 24,
                          borderRadius: 1.5,
                          color: 'text.secondary',
                          borderColor: alpha(theme.palette.divider, 0.5)
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid2>
              
              {/* Property Details */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Grid2 container spacing={2}>
                  {homeInfo.bedrooms > 0 && (
                    <Grid2 size={6}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }}>
                        <BedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="600" color="text.primary">
                            {homeInfo.bedrooms}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Beds
                          </Typography>
                        </Box>
                      </Box>
                    </Grid2>
                  )}
                  {homeInfo.bathrooms > 0 && (
                    <Grid2 size={6}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }}>
                        <BathtubIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="600" color="text.primary">
                            {homeInfo.bathrooms}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Baths
                          </Typography>
                        </Box>
                      </Box>
                    </Grid2>
                  )}
                  {homeInfo.sqft > 0 && (
                    <Grid2 size={6}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }}>
                        <SquareFootIcon sx={{ fontSize: 18, color: 'info.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="600" color="text.primary">
                            {homeInfo.sqft.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Sq Ft
                          </Typography>
                        </Box>
                      </Box>
                    </Grid2>
                  )}
                  {homeInfo.yearBuilt && (
                    <Grid2 size={6}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }}>
                        <CalendarTodayIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="600" color="text.primary">
                            {homeInfo.yearBuilt}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Built
                          </Typography>
                        </Box>
                      </Box>
                    </Grid2>
                  )}
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
        </Box>

        {/* Analytics Content */}
        <Grid2 container spacing={3}>
          {/* Summary Section */}
          <Grid2 size={12}>
            <SummarySection analytics={clientAnalytics} selectedPeriod={selectedPeriod} />
          </Grid2>

          {/* Engagement Section */}
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <EngagementSection analytics={clientAnalytics} selectedPeriod={selectedPeriod} />
          </Grid2>

          {/* Area Interaction */}
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <AreaInteractionSection analytics={clientAnalytics} />
          </Grid2>

          {/* Image Analytics */}
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <ImageAnalyticsSection analytics={clientAnalytics} />
          </Grid2>


        </Grid2>
      </Container>
    </BasePage>
  );
};

// Premium Summary Section with key metrics
const SummarySection = ({ analytics, selectedPeriod }: { analytics: ClientAnalytics | null; selectedPeriod: number }) => {
  const theme = useTheme();
  
  const getComparisonPeriodText = (days: number) => {
    switch (days) {
      case 7: return 'from last week';
      case 30: return 'from last month';
      case 180: return 'from prev 6 months';
      default: return `from prev ${days} days`;
    }
  };
  
  const totalViews = analytics?.summary?.totalHomeOpens || 0;
  const previousViews = analytics?.summary?.previousTotalHomeOpens || 0;
  
  // Calculate trend
  const viewsDifference = totalViews - previousViews;
  const comparisonPeriod = getComparisonPeriodText(selectedPeriod);
  const trendText = viewsDifference > 0 
    ? `+${viewsDifference} ${comparisonPeriod}` 
    : viewsDifference < 0 
    ? `${viewsDifference} ${comparisonPeriod}` 
    : `No change ${comparisonPeriod}`;

  // Calculate last viewed date from daily engagement data
  const lastViewedData = useMemo(() => {
    if (!analytics?.dailyEngagement || analytics.dailyEngagement.length === 0) {
      return { value: "No data", trend: "Never" };
    }

    // Get the most recent engagement date
    const sortedDates = analytics.dailyEngagement
      .map(engagement => new Date(engagement.eventDate))
      .sort((a, b) => b.getTime() - a.getTime());

    if (sortedDates.length === 0) {
      return { value: "No data", trend: "Never" };
    }

    const lastViewedDate = sortedDates[0];
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - lastViewedDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    let value: string;
    let trend: string;

    if (diffDays === 0) {
      value = "Today";
      trend = "Very Recent";
    } else if (diffDays === 1) {
      value = "Yesterday";
      trend = "Recent";
    } else if (diffDays <= 7) {
      value = `${diffDays} days ago`;
      trend = "Recent";
    } else if (diffDays <= 30) {
      value = `${diffDays} days ago`;
      trend = "Moderate";
    } else {
      value = `${diffDays} days ago`;
      trend = "Stale";
    }

    return { value, trend };
  }, [analytics]);

  // Calculate dynamic engagement score based on engagement data
  const engagementScore = useMemo(() => {
    if (!analytics) {
      return { value: "0.0", trend: "No data" };
    }

    let score = 0;
    const maxScore = 10;

    // 1. Daily Engagement Consistency (0-3 points)
    // More consistent engagement across days = higher score
    const activeDays = analytics.dailyEngagement?.filter(d => d.engagementTimeMs > 0).length || 0;
    const consistencyScore = Math.min(3, activeDays * 0.5); // Max 3 points for 6+ active days
    score += consistencyScore;

    // 2. Total Engagement Time (0-3 points)
    // Higher total engagement time = higher score
    const totalEngagementMs = analytics.summary?.totalEngagementTimeAllDays || 0;
    const engagementMinutes = totalEngagementMs / 60000;
    const timeScore = Math.min(3, engagementMinutes / 10); // Max 3 points for 30+ minutes total
    score += timeScore;

    // 3. Area Exploration Depth (0-2 points)
    // More areas explored = higher score
    const areasEngaged = analytics.summary?.totalAreasEngaged || 0;
    const areaScore = Math.min(2, areasEngaged * 0.4); // Max 2 points for 5+ areas
    score += areaScore;

    // 4. Image Engagement (0-2 points)
    // More images engaged with = higher score
    const imagesEngaged = analytics.summary?.uniqueImages || 0;
    const imageScore = Math.min(2, imagesEngaged * 0.2); // Max 2 points for 10+ images
    score += imageScore;

    // Convert to 0-10 scale and format
    const finalScore = Math.min(maxScore, score);
    const formattedScore = finalScore.toFixed(1);

    // Determine trend based on score
    let trend: string;
    if (finalScore >= 8) {
      trend = "Excellent";
    } else if (finalScore >= 6) {
      trend = "Good";
    } else if (finalScore >= 4) {
      trend = "Moderate";
    } else if (finalScore >= 2) {
      trend = "Low";
    } else {
      trend = "Minimal";
    }

    return { value: formattedScore, trend };
  }, [analytics]);

  // Calculate dynamic priority level based on engagement score and other factors
  const priorityLevel = useMemo(() => {
    if (!analytics) {
      return { value: "Unknown", trend: "No data", color: theme.palette.grey[500] };
    }

    const score = parseFloat(engagementScore.value);
    const activeDays = analytics.dailyEngagement?.filter(d => d.engagementTimeMs > 0).length || 0;
    const totalViews = analytics.summary?.totalHomeOpens || 0;
    const recentEngagement = analytics.dailyEngagement?.some(d => {
      const engagementDate = new Date(d.eventDate);
      const daysDiff = Math.floor((new Date().getTime() - engagementDate.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff <= 2 && d.engagementTimeMs > 0;
    }) || false;

    let priority: string;
    let trend: string;
    let color: string;

    // High Priority: High interaction score + recent activity
    if (score >= 7 && recentEngagement) {
      priority = "Hot Lead";
      trend = "Act Now";
      color = theme.palette.error.main;
    }
    // High Priority: Good score with consistent engagement
    else if (score >= 6 && activeDays >= 3) {
      priority = "Premium";
      trend = "High Interest";
      color = theme.palette.error.main;
    }
    // Medium-High Priority: Good recent engagement but lower consistency
    else if (score >= 5 && recentEngagement) {
      priority = "Warm Lead";
      trend = "Follow Up";
      color = theme.palette.warning.main;
    }
    // Medium Priority: Decent score but not recent
    else if (score >= 4) {
      priority = "Standard";
      trend = "Moderate";
      color = theme.palette.info.main;
    }
    // Low Priority: Some engagement but minimal
    else if (score >= 2 || totalViews > 0) {
      priority = "Basic";
      trend = "Low Priority";
      color = theme.palette.grey[600];
    }
    // Very Low Priority: Minimal to no engagement
    else {
      priority = "Cold";
      trend = "Inactive";
      color = theme.palette.grey[500];
    }

    return { value: priority, trend, color };
  }, [analytics, engagementScore.value, theme]);

  // Calculate return rate based on viewing patterns
  const returnRate = useMemo(() => {
    if (!analytics?.dailyEngagement || analytics.dailyEngagement.length === 0) {
      return { value: "0%", trend: "No returns" };
    }

    const activeDays = analytics.dailyEngagement.filter(d => d.engagementTimeMs > 0).length;
    const totalViews = analytics.summary?.totalHomeOpens || 0;
    
    // If they've only viewed once, return rate is 0%
    if (totalViews <= 1) {
      return { value: "0%", trend: "First visit" };
    }

    // Calculate return rate as percentage of additional visits beyond the first
    const returnVisits = Math.max(0, totalViews - 1);
    const returnPercentage = Math.min(100, Math.round((returnVisits / Math.max(1, totalViews)) * 100));
    
    let trend: string;
    if (returnPercentage >= 80) {
      trend = "Very High";
    } else if (returnPercentage >= 60) {
      trend = "High";
    } else if (returnPercentage >= 40) {
      trend = "Moderate";
    } else if (returnPercentage >= 20) {
      trend = "Low";
    } else {
      trend = "Very Low";
    }

    // Factor in consistency - multiple days of engagement indicates stronger return behavior
    if (activeDays >= 3 && returnPercentage >= 40) {
      trend = "Consistent";
    }

    return { value: `${returnPercentage}%`, trend };
  }, [analytics]);
  
  const metrics = [
    { 
      title: "Total Views", 
      value: totalViews.toString(), 
      icon: VisibilityIcon, 
      color: theme.palette.primary.main,
      trend: totalViews > 0 ? trendText : "No data",
      bgColor: alpha(theme.palette.primary.main, 0.1)
    },
    { 
      title: "Last Viewed", 
      value: lastViewedData.value, 
      icon: AccessTimeIcon, 
      color: theme.palette.warning.main,
      trend: lastViewedData.trend,
      bgColor: alpha(theme.palette.warning.main, 0.1)
    },
    { 
      title: "Engagement Score", 
      value: engagementScore.value, 
      icon: TrendingUpIcon, 
      color: theme.palette.success.main,
      trend: engagementScore.trend,
      bgColor: alpha(theme.palette.success.main, 0.1)
    },
    { 
      title: "Return Rate", 
      value: returnRate.value, 
      icon: TrendingUpIcon, 
      color: theme.palette.secondary.main,
      trend: returnRate.trend,
      bgColor: alpha(theme.palette.secondary.main, 0.1)
    },
    { 
      title: "Priority Level", 
      value: priorityLevel.value, 
      icon: AssessmentIcon, 
      color: priorityLevel.color,
      trend: priorityLevel.trend,
      bgColor: alpha(priorityLevel.color, 0.1)
    }
  ];

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        p: 3, 
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.primary.main, 0.1) 
          }}>
            <AssessmentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Performance Overview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Key engagement metrics and performance indicators
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <CardContent sx={{ p: 0, pb: 2 }}>
        <Grid2 container>
          {metrics.map((metric, index) => (
            <Grid2 key={metric.title} size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{ 
                p: 3,
                borderRight: index < metrics.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                borderBottom: { xs: `1px solid ${alpha(theme.palette.divider, 0.1)}`, md: 'none' },
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.02)
                },
                transition: 'all 0.2s ease'
              }}>
                <PremiumStatBlock {...metric} />
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </CardContent>
    </Paper>
  );
};

const PremiumStatBlock = ({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  trend, 
  bgColor 
}: { 
  title: string; 
  value: string; 
  icon: any; 
  color: string; 
  trend: string;
  bgColor: string;
}) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ 
      display: 'inline-flex',
      p: 1.5,
      borderRadius: 2,
      bgcolor: bgColor,
      mb: 2
    }}>
      <Icon sx={{ fontSize: 20, color }} />
    </Box>
    <Typography 
      variant="h4" 
      fontWeight="bold" 
      color="text.primary" 
      sx={{ mb: 0.5, letterSpacing: '-0.01em' }}
    >
      {value}
    </Typography>
    <Typography 
      variant="caption" 
      color="text.secondary" 
      sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}
    >
      {title}
    </Typography>
    <Chip 
      label={trend}
      size="small"
      sx={{ 
        fontSize: '0.7rem',
        fontWeight: 600,
        bgcolor: alpha(color, 0.1),
        color: color,
        border: 'none'
      }}
      variant="outlined"
    />
  </Box>
);



// Premium Engagement Patterns
const EngagementSection = ({ analytics, selectedPeriod }: { analytics: ClientAnalytics | null; selectedPeriod: number }) => {
  const theme = useTheme();
  
  // Process real data from analytics or use fallback
  const data = useMemo(() => {
    if (!analytics?.dailyEngagement || analytics.dailyEngagement.length === 0) {
      // Fallback data when no real data is available - adjust based on period
      const fallbackCount = selectedPeriod <= 7 ? 7 : selectedPeriod <= 30 ? 14 : 20; // Show max visual days
      const fallbackData = [];
      for (let i = 0; i < fallbackCount; i++) {
        fallbackData.push({ 
          day: `D${i + 1}`, 
          views: 0, 
          duration: '0s', 
          engagementMs: 0 
        });
      }
      return fallbackData;
    }

    // Create appropriate time periods based on selection
    const today = new Date();
    const periods: Array<{
      date: string;
      day: string;
      views: number;
      engagementMs: number;
      isWeek?: boolean;
      isMonth?: boolean;
      weekStart?: string;
      weekEnd?: string;
      monthStart?: string;
      monthEnd?: string;
    }> = [];
    
    if (selectedPeriod <= 7) {
      // Show daily for 7 days or less
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      for (let i = selectedPeriod - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateString = date.toISOString().split('T')[0];
        const dayName = dayNames[date.getDay()];
        
        periods.push({
          date: dateString,
          day: dayName,
          views: 0,
          engagementMs: 0
        });
      }
    } else if (selectedPeriod <= 30) {
      // Show weekly for 1 month (show last 4 weeks)
      for (let i = 3; i >= 0; i--) {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - (i * 7) - 6);
        const weekEnd = new Date(today);
        weekEnd.setDate(today.getDate() - (i * 7));
        
        // Format week start date as "M/d" like iOS implementation
        const weekFormatter = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`;
        
        periods.push({
          date: `${weekStart.toISOString().split('T')[0]}_${weekEnd.toISOString().split('T')[0]}`,
          day: weekFormatter,
          views: 0,
          engagementMs: 0,
          isWeek: true,
          weekStart: weekStart.toISOString().split('T')[0],
          weekEnd: weekEnd.toISOString().split('T')[0]
        });
      }
    } else {
      // Show monthly for 6 months (show last 6 months)
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(today);
        monthDate.setMonth(today.getMonth() - i);
        const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
        const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).toISOString().split('T')[0];
        const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).toISOString().split('T')[0];
        
        periods.push({
          date: `${monthStart}_${monthEnd}`,
          day: monthName,
          views: 0,
          engagementMs: 0,
          isMonth: true,
          monthStart,
          monthEnd
        });
      }
    }

    // Map analytics data to the periods
    analytics.dailyEngagement.forEach(engagement => {
      if (selectedPeriod <= 7) {
        // Daily mapping
        const dayEntry = periods.find(d => d.date === engagement.eventDate);
        if (dayEntry) {
          dayEntry.views = 1;
          dayEntry.engagementMs = engagement.engagementTimeMs;
        }
      } else {
        // Weekly or monthly aggregation
        const engagementDate = engagement.eventDate;
        const matchingPeriod = periods.find(p => {
          if (p.isWeek && p.weekStart && p.weekEnd) {
            return engagementDate >= p.weekStart && engagementDate <= p.weekEnd;
          } else if (p.isMonth && p.monthStart && p.monthEnd) {
            return engagementDate >= p.monthStart && engagementDate <= p.monthEnd;
          }
          return false;
        });
        
        if (matchingPeriod) {
          matchingPeriod.views += 1;
          matchingPeriod.engagementMs += engagement.engagementTimeMs;
        }
      }
    });

    // Convert to the format expected by the chart
    return periods.map(period => ({
      day: period.day,
      views: period.engagementMs, // Use raw milliseconds for proportional heights
      duration: period.engagementMs > 0 ? 
        `${Math.floor(period.engagementMs / 60000)}m ${Math.floor((period.engagementMs % 60000) / 1000)}s` : 
        '0s',
      engagementMs: period.engagementMs
    }));
  }, [analytics, selectedPeriod]);

  const totalEngagementMs = data.reduce((sum, item) => sum + item.engagementMs, 0);
  const maxViews = Math.max(...data.map(item => item.views), 1); // Ensure at least 1 to avoid division by zero
  const hasData = analytics?.dailyEngagement && analytics.dailyEngagement.length > 0;
  const totalMinutes = Math.floor(totalEngagementMs / 60000);
  const totalSeconds = Math.floor((totalEngagementMs % 60000) / 1000);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        p: 3, 
        bgcolor: alpha(theme.palette.secondary.main, 0.02),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.secondary.main, 0.1) 
          }}>
            <AnalyticsIcon sx={{ color: 'secondary.main', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Engagement Patterns
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {selectedPeriod <= 7 ? 'Daily viewing activity and session length' :
               selectedPeriod <= 30 ? 'Weekly engagement trends and patterns' :
               'Monthly engagement overview and trends'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" fontWeight="bold" color="secondary.main">
              {totalMinutes > 0 ? `${totalMinutes}m` : `${totalSeconds}s`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Time
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <CardContent sx={{ p: 2.5, pb: 3, height: 'calc(100% - 120px)', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'end', gap: 1.5, flex: 1, minHeight: 120, mb: 2 }}>
          {data.map((item) => (
            <Box key={item.day} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, height: '100%' }}>
              <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'end', width: '100%', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: 24,
                    height: `${(item.views / maxViews) * 100}%`,
                    borderRadius: '6px 6px 3px 3px',
                    background: `linear-gradient(to top, ${theme.palette.secondary.main}, ${alpha(theme.palette.secondary.main, 0.7)})`,
                    position: 'relative',
                    '&:hover': {
                      '&::after': {
                        content: `"${item.duration}"`,
                        position: 'absolute',
                        top: -22,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'black',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }
                    }
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '0.65rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.engagementMs > 0 ? `${Math.floor(item.engagementMs / 60000)}m` : '0'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
                {item.day}
              </Typography>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          {hasData ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Peak: <strong style={{ color: theme.palette.secondary.main }}>
                {Math.max(...data.map(d => Math.floor(d.engagementMs / 60000)))}m
              </strong> • Active days: <strong>{data.filter(d => d.engagementMs > 0).length}</strong>
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              No engagement data available for the last 7 days
            </Typography>
          )}
        </Box>
      </CardContent>
    </Paper>
  );
};

// Premium Area Interaction Depth
const AreaInteractionSection = ({ analytics }: { analytics: ClientAnalytics | null }) => {
  const theme = useTheme();
  
  // Area icons mapping
  const areaIcons: Record<string, string> = {
    'Kitchen': '🍳',
    'Bathrooms': '🚿',
    'Bathroom': '🚿',
    'Primary Suite': '🛏️',
    'Bedroom': '🛏️',
    'Living Room': '🛋️',
    'Living': '🛋️',
    'Backyard': '🌳',
    'Yard': '🌳',
    'Garden': '🌳',
    'Front Porch': '🏠',
    'Front of Home': '🏠',
    'Dining Room': '🍽️',
    'Dining': '🍽️',
    'Office': '💼',
    'Study': '📚',
    'Garage': '🚗',
    'Basement': '🏠',
    'Attic': '🏠',
    'Laundry': '🧺',
    'Utility': '🔧'
  };

  // Area colors
  const areaColors = [
    '#FF9800', '#2196F3', '#4CAF50', '#607D8B', '#F44336',
    '#9C27B0', '#00BCD4', '#FF5722', '#795548', '#607D8B'
  ];

  // Calculate areas from real API response
  const areas = useMemo(() => {
    if (!analytics || !analytics.areaEngagement || analytics.areaEngagement.length === 0) {
      return [];
    }

    return analytics.areaEngagement
      .sort((a, b) => b.engagementTimeMs - a.engagementTimeMs)
      .map((area, index) => ({
        name: area.areaName,
        seconds: Math.round(area.engagementTimeMs / 1000),
        color: areaColors[index % areaColors.length],
        icon: areaIcons[area.areaName] || '🏠',
        rank: index + 1,
        viewCount: area.viewCount,
        dismissalReasons: area.dismissalReasons
      }));
  }, [analytics]);

  const maxSeconds = areas.length > 0 ? Math.max(...areas.map(area => area.seconds)) : 0;
  const totalSeconds = areas.reduce((sum, area) => sum + area.seconds, 0);
  const hasData = areas.length > 0;

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        p: 3, 
        bgcolor: alpha(theme.palette.warning.main, 0.02),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.warning.main, 0.1) 
          }}>
            <VisibilityIcon sx={{ color: 'warning.main', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Area Focus Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Time spent exploring different property areas
            </Typography>
          </Box>
          <Chip 
            label={hasData ? `${areas[0]?.name} Leading` : "No Data"}
            color={hasData ? "warning" : "default"}
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        </Box>
      </Box>
      
      <CardContent sx={{ p: 3, pb: 4 }}>
        {!hasData ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No area engagement data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Area engagement tracking will show up here once users start viewing area details
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={3}>
              {areas.map((area) => (
            <Box key={area.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  {area.icon}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body1" fontWeight="bold">
                      {area.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={`#${area.rank}`}
                        size="small"
                        sx={{ 
                          bgcolor: alpha(area.color, 0.1),
                          color: area.color,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          minWidth: 28
                        }}
                      />
                      <Typography variant="body1" fontWeight="bold" sx={{ color: area.color }}>
                        {area.seconds}s
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(area.seconds / maxSeconds) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: alpha(area.color, 0.15),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: area.color,
                        borderRadius: 5,
                        background: `linear-gradient(90deg, ${area.color} 0%, ${alpha(area.color, 0.8)} 100%)`,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
            </Stack>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total exploration time: <strong style={{ color: theme.palette.warning.main }}>
                  {Math.floor(totalSeconds / 60)}m {totalSeconds % 60}s
                </strong> • Most engaging: <strong>{areas[0]?.name}</strong>
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Paper>
  );
};


// Premium Image Analytics
const ImageAnalyticsSection = ({ analytics }: { analytics: ClientAnalytics | null }) => {
  const theme = useTheme();
  
  // Calculate analytics data from real API response
  const imageStats = useMemo(() => {
    if (!analytics || !analytics.imageEngagement || analytics.imageEngagement.length === 0) {
      return {
        totalEngagementTimeMs: 0,
        uniqueImages: 0,
        avgEngagementTimeMs: 0,
        topImages: []
      };
    }

    const { imageEngagement, summary } = analytics;
    
    // Top performing images (sorted by engagement time)
    const topImages = imageEngagement
      .sort((a, b) => b.engagementTimeMs - a.engagementTimeMs)
      .slice(0, 10) // Show more images in the carousel
      .map((item, index) => ({
        name: `Image ${index + 1}`,
        engagementTimeMs: item.engagementTimeMs,
        url: item.imageUrl
      }));

    return {
      totalEngagementTimeMs: summary.totalEngagementTimeMs,
      uniqueImages: summary.uniqueImages,
      avgEngagementTimeMs: summary.totalEngagementTimeMs / Math.max(1, imageEngagement.length),
      topImages
    };
  }, [analytics]);
  
  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        p: 3, 
        bgcolor: alpha(theme.palette.grey[500], 0.02),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.grey[500], 0.1) 
          }}>
            <PhotoIcon sx={{ color: theme.palette.grey[600], fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Visual Engagement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Photo interaction and viewing patterns
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <CardContent sx={{ p: 0, pb: 2 }}>
        <Grid2 container>
          <Grid2 size={{ xs: 6 }}>
            <Box sx={{ 
              p: 2,
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              },
              transition: 'all 0.2s ease'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  fontWeight="bold" 
                  color="text.primary" 
                  sx={{ mb: 1, letterSpacing: '-0.01em' }}
                >
                  {Math.round(imageStats.avgEngagementTimeMs / 1000)}s
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ fontWeight: 600 }}
                >
                  Average Engagement
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  per image
                </Typography>
              </Box>
            </Box>
          </Grid2>
          
          <Grid2 size={{ xs: 6 }}>
            <Box sx={{ 
              p: 2,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              },
              transition: 'all 0.2s ease'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  fontWeight="bold" 
                  color="text.primary" 
                  sx={{ mb: 1, letterSpacing: '-0.01em' }}
                >
                  {Math.round(imageStats.totalEngagementTimeMs / 1000)}s
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ fontWeight: 600 }}
                >
                  Total Engagement
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  all images
                </Typography>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
        
        <Divider sx={{ my: 3 }} />
        
        <Box>
          <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            Most Engaged Images
          </Typography>
          <ImageEngagementGallery 
            images={imageStats.topImages}
            showRanking={false}
          />
        </Box>
      </CardContent>
    </Paper>
  );
};



export default HomeAnalytics;